/**
 * @architecture-header 1
 * @layer module
 * @domain classrooms
 * @responsibility Render the student's guardian invite page and the parent walkthrough.
 * @state-owner dataset store holds guardian records
 * @allowed-imports React; dataset and workspace stores; shared components, selectors and hooks
 * @forbidden teacher-only controls; direct API client
 * @public-api workspace main column
 * @side-effects toasts and dialog opening
 * @tests workspace Playwright flows in frontend/apps/web/e2e/workspace.spec.ts
 */

import { SectionLabel } from '../../../shared/components/Modal';
import { Page, PageBody, PageHeader } from '../../../shared/components/PageShell';
import { QrCode } from '../../../shared/components/QrCode';
import { StatusText } from '../../../shared/components/StatusIcon';
import { ME } from '../../../shared/lib/demo-data';
import type { GuardianChannel } from '../../../shared/lib/model';
import { guardianCode, guardianLink } from '../../../shared/lib/selectors';
import { useDatasetStore } from '../../../shared/model/dataset-store';
import { useTranslator } from '../../../shared/model/use-workspace';
import { useWorkspaceUiStore } from '../../../shared/model/workspace-store';
import { CHANNEL_BINDING, CHANNEL_COLOR, CHANNEL_LABEL } from '../model/guardian';
import { ChannelBadge } from './GuardianDialog';

const CHANNELS: readonly GuardianChannel[] = ['line', 'wa', 'wechat', 'sms'];

export function GuardianInvitePage() {
  const t = useTranslator();
  const notify = useWorkspaceUiStore((state) => state.notify);
  const guardians = useDatasetStore((state) => state.guardians[ME] ?? []);
  const linked = guardians.filter((record) => record.state === 'linked');

  return (
    <Page maxWidth={900}>
      <PageHeader meta={t('讓家長收到你的學習報告')} title={t('邀請我的家長')} />
      <PageBody>
        {/* One 560px column would oversize the QR and leave the
            right half of the canvas empty. Two panes keep the QR at its designed width and give the
            binding status and the parent walkthrough the space they were missing. */}
        <div className="gpanes">
          <div>
            <div className="vqrbox">
              <div className="vqrbrand">
                <span className="vqrlogo">V</span>
                {`${ME} ${t('的家長')}`}
              </div>
              <div className="vqrimg">
                <QrCode seed={guardianLink(ME)} />
              </div>
              <div className="gcode">
                <span>{t('綁定碼')}</span>
                <b>{guardianCode(ME)}</b>
              </div>
            </div>
            <div className="grow2">
              <button
                className="bt pri"
                type="button"
                onClick={() => notify(t('已開啟分享，選一個聊天軟體傳給家長'))}
              >
                {t('傳給家長')}
              </button>
              <button className="bt" type="button" onClick={() => notify(t('已複製連結'))}>
                {t('複製連結')}
              </button>
            </div>
            <div className="gexp">
              {t(
                '你不用輸入家長的手機或帳號。把下面的 QR 或連結給家長，聯絡方式由家長自己在頁面上填，這樣才不會填錯，也不會有人代填別人的個資。',
              )}
            </div>
          </div>
          <div>
            <SectionLabel>{t('目前已綁定')}</SectionLabel>
            {linked.length ? (
              <div className="glist">
                {linked.map((record) => (
                  <div className="vgrow" key={`${record.relation}-${record.address}`}>
                    <span className="gav">{record.relation[0]}</span>
                    <span className="gt">
                      <b>{record.relation}</b>
                      <span>
                        <ChannelBadge channel={record.channel} />
                        {` ${record.address}`}
                      </span>
                    </span>
                    <StatusText glyph="done" label={t('已綁定')} />
                  </div>
                ))}
              </div>
            ) : (
              <div className="vguide">
                <b>{t('還沒有家長綁定')}</b>
                <span>{t('家長綁定之後，老師發的報告他就收得到。')}</span>
              </div>
            )}
            <SectionLabel>{t('家長會看到的畫面')}</SectionLabel>
            <GuardianSteps student={ME} />
          </div>
        </div>
      </PageBody>
    </Page>
  );
}

/** inlined on the invite page instead of behind a modal. */
function GuardianSteps({ student }: { student: string }) {
  const t = useTranslator();
  const classes = useDatasetStore((state) => state.classes);

  return (
    <>
      <div className="gstep">
        <span className="gsn">1</span>
        <div>
          <b>{t('確認身分')}</b>
          <div className="gscr">
            <p>{`${t('你是')} ${student} ${t('的家長嗎？')}`}</p>
            <p className="sub">{`${classes[0].name} · ${t('王維青老師')}`}</p>
            <div className="grow2">
              <button className="bt pri" type="button">
                {t('是，我是')}
              </button>
              <button className="bt" type="button">
                {t('我不是')}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="gstep">
        <span className="gsn">2</span>
        <div>
          <b>{t('填自己是誰')}</b>
          <div className="gscr">
            <p className="sub">{t('姓名與關係，由家長自己填')}</p>
            <div className="gfk">{`${t('陳美惠')} · ${t('媽媽')}`}</div>
          </div>
        </div>
      </div>
      <div className="gstep">
        <span className="gsn">3</span>
        <div>
          <b>{t('選自己要用哪個管道收')}</b>
          <div className="gscr">
            {CHANNELS.map((channel) => (
              <div className="gcb" key={channel}>
                <span className="chb" style={{ ['--c' as string]: CHANNEL_COLOR[channel] }}>
                  {t(CHANNEL_LABEL[channel])}
                </span>
                <b>{t(CHANNEL_BINDING[channel][0])}</b>
                <em>{t(CHANNEL_BINDING[channel][1])}</em>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}
